import { Article, HelpOutline, LocalOffer, PersonAdd, SupportAgent } from "@mui/icons-material";
import { Button, Chip, MenuItem, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { eventClick, RefreshData } from "components/helper";
import { P } from "const/permissions";
import { usePermission } from "hooks/usePermissions";
import { useDebouncedValue } from "hooks/useDebouncedValue";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { InvitationRowActions, useInvitationActions } from "./InvitationActions";
import InviteReferralDrawer from "./InviteReferralDrawer";
import QueueCountBadge from "./QueueCountBadge";
import ReferralProgramContentDrawer from "./ReferralProgramContentDrawer";
import {
  invitationCaption,
  listPartners,
  partnerStatusLabel,
  partnerStatusTone,
} from "./referralPartnersApi";
import { useReferralOverview } from "./referralOverviewApi";

/** A queue button names its queue and wears its pending number as a badge; zero hides it. */
const withPendingCount = (label, count) => (
  <>
    {label} <QueueCountBadge count={count} />
  </>
);

const STATUSES = ["", "PRE_ENROLMENT", "INVITED", "ONBOARDING", "ACTIVE", "SUSPENDED", "ARCHIVED"];

const money = (value) =>
  value === null || value === undefined
    ? "—"
    : `$${Number(value).toLocaleString("en-AU", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

const Referral = () => {
  const navigate = useNavigate();
  const canInvite = usePermission(P.REFERRAL.PARTNER_INVITE);
  const canReadSupport = usePermission(P.REFERRAL.MESSAGE_READ);
  const canViewCommission = usePermission(P.REFERRAL.COMMISSION_READ);
  const canAssignReferrers = usePermission(P.REFERRAL.ATTRIBUTION_ASSIGN);
  const canReadApplicationRequests = usePermission(P.REFERRAL.APPLICATION_READ);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [contentOpen, setContentOpen] = useState(false);
  const [contentTab, setContentTab] = useState("offers");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 20 });
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const debouncedSearch = useDebouncedValue(search, 400);

  const openContent = (tab) => {
    setContentTab(tab);
    setContentOpen(true);
  };

  const queryParams = useMemo(
    () => ({
      page: pagination.pageIndex,
      size: pagination.pageSize,
      search: debouncedSearch,
      status,
    }),
    [pagination.pageIndex, pagination.pageSize, debouncedSearch, status]
  );

  // Forced fresh on mount: the app QueryClient sets refetchOnMount: false globally, so an
  // invalidateQueries from the invite drawer would not refresh this list on its own.
  const { data, isLoading, isFetching, refetch } = useQuery(
    ["referral-partners", queryParams],
    () => listPartners(queryParams),
    { keepPreviousData: true, refetchOnMount: true }
  );

  const invitationActions = useInvitationActions(refetch);

  // Shared cache key with the sidebar: the topbar numbers ride on the same single request.
  const { data: overview } = useReferralOverview();

  const columns = useMemo(
    () => [
      {
        accessorKey: "displayName",
        header: "Name",
        size: 150,
      },
      {
        accessorKey: "email",
        header: "Email",
        size: 180,
      },
      {
        accessorKey: "phone",
        header: "Phone",
        size: 130,
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 160,
        Cell: ({ row }) => {
          const caption = invitationCaption(row.original.invitation);
          return (
            <div className="flex flex-col items-start gap-0.5">
              <Chip
                label={partnerStatusLabel(row.original.status)}
                color={partnerStatusTone(row.original.status)}
                size="small"
              />
              {caption ? (
                <span
                  className={
                    row.original.invitation.status === "EXPIRED"
                      ? "text-xs text-amber-700"
                      : "text-xs text-app-text-secondary"
                  }
                >
                  {caption}
                </span>
              ) : null}
            </div>
          );
        },
      },
      {
        accessorKey: "clientCount",
        header: "Clients",
        size: 80,
      },
      ...(canViewCommission
        ? [
            {
              accessorKey: "totalAccrued",
              header: "Accrued",
              size: 110,
              Cell: ({ row }) => money(row.original.totalAccrued),
            },
            {
              accessorKey: "totalApproved",
              header: "Approved",
              size: 110,
              Cell: ({ row }) => money(row.original.totalApproved),
            },
            {
              accessorKey: "totalPaid",
              header: "Paid",
              size: 110,
              Cell: ({ row }) => money(row.original.totalPaid),
            },
          ]
        : []),
      ...(canInvite
        ? [
            {
              id: "invitation",
              header: "Invitation",
              size: 100,
              enableSorting: false,
              Cell: ({ row }) => (
                <InvitationRowActions partner={row.original} onAsk={invitationActions.ask} />
              ),
            },
          ]
        : []),
    ],
    [canViewCommission, canInvite, invitationActions.ask]
  );

  const table = useMaterialReactTable({
    columns,
    data: data?.content || [],
    rowCount: data?.totalItems || 0,
    manualPagination: true,
    onPaginationChange: setPagination,
    state: {
      pagination,
      isLoading,
      showProgressBars: isFetching,
    },
    rowNumberDisplayMode: "original",
    enableRowNumbers: true,
    enableStickyHeader: true,
    muiTableContainerProps: {
      sx: {
        maxHeight: "70vh",
        minHeight: "70vh",
        overflowY: "auto",
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (event) => {
        if (!eventClick(event)) {
          navigate(`/admin/admin-referral/partners/${row.original.id}`);
        }
      },
      sx: {
        cursor: "pointer",
        "&:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.04)",
        },
      },
    }),
  });

  return (
    <div className="flex flex-wrap mt-4 dashBody">
      <div className="w-full mb-12 px-4">
        <div
          className={
            "relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded " +
            "bg-app-surface text-app-text"
          }
        >
          <div className="rounded-t mb-0 px-4 py-3 border-0">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h3 className={"font-semibold text-lg " + "text-app-text"}>
                Referral partners ({data?.totalItems ?? 0}) <RefreshData refreshData={refetch} />
              </h3>
              <div className="flex flex-wrap items-center gap-2">
                {canReadSupport ? (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<SupportAgent />}
                    onClick={() => navigate("/admin/admin-referral/support")}
                  >
                    {withPendingCount("Support tickets", overview?.openSupportTickets)}
                  </Button>
                ) : null}
                {canAssignReferrers ? (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => navigate("/admin/admin-referral/referrer-review")}
                  >
                    Referrer review
                  </Button>
                ) : null}
                {canReadApplicationRequests ? (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => navigate("/admin/admin-referral/application-requests")}
                  >
                    {withPendingCount("Application requests", overview?.pendingApplicationRequests)}
                  </Button>
                ) : null}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<LocalOffer />}
                  onClick={() => openContent("offers")}
                >
                  {withPendingCount("Offers", overview?.runningOffers)}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Article />}
                  onClick={() => openContent("terms")}
                >
                  Terms
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<HelpOutline />}
                  onClick={() => openContent("faq")}
                >
                  FAQ
                </Button>
                {canInvite ? (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<PersonAdd />}
                    onClick={() => setInviteOpen(true)}
                  >
                    Invite referral
                  </Button>
                ) : null}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-3">
              <TextField
                size="small"
                label="Search name, phone or email"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                sx={{ minWidth: 260 }}
              />
              <TextField
                size="small"
                select
                label="Status"
                value={status}
                onChange={(event) => setStatus(event.target.value)}
                sx={{ minWidth: 160 }}
              >
                {STATUSES.map((option) => (
                  <MenuItem key={option || "all"} value={option}>
                    {option ? partnerStatusLabel(option) : "All"}
                  </MenuItem>
                ))}
              </TextField>
            </div>
          </div>
          <div className="block w-full overflow-x-auto">
            <MaterialReactTable table={table} />
          </div>
        </div>
      </div>

      {invitationActions.dialog}

      <InviteReferralDrawer
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onInvited={() => refetch()}
      />
      <ReferralProgramContentDrawer
        open={contentOpen}
        onClose={() => setContentOpen(false)}
        initialTab={contentTab}
      />
    </div>
  );
};

export default Referral;
